import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppBar, IconButton, InputBase, Toolbar, Typography, Badge, Menu, MenuItem, Popover, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { toggleNavDrawer, toggleNavCollapsed, toggleTheme } from '../redux/slices/ui';
import { search } from '../redux/ducks/search';
import {
    getDailyActivityAiSummary,
    requestDailyActivityAiSummary,
    getWeeklyActivityAiSummary,
    requestWeeklyActivityAiSummary,
    getMonthlyActivityAiSummary,
    requestMonthlyActivityAiSummary,
    getEndOfDayChecklist,
} from '../redux/ducks/ai';
import { formatDayLong } from '../utils/dateUtils';
import { grey } from '@mui/material/colors';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircle from '@mui/icons-material/AccountCircle';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Brightness4 from '@mui/icons-material/Brightness4';
import Brightness7 from '@mui/icons-material/Brightness7';
import styled from '@emotion/styled';
import { alpha } from '@mui/system';
import NotificationPanel from './NotificationPanel';
import SearchModal from './SearchModal';
import AIResponseDialog from './AIResponseDialog';
import ActivitySummaryPicker from './ActivitySummaryPicker';
import { useHasPermission } from '../utils/permissionUtils';
import { useBrandName } from '../utils/brandUtils';
import { getSocket } from '../services/socketService';

const sundayForMondayLocal = (monday) => {
    const [y, m, d] = monday.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + 6);
    const ey = dt.getFullYear();
    const em = String(dt.getMonth() + 1).padStart(2, '0');
    const ed = String(dt.getDate()).padStart(2, '0');
    return `${ey}-${em}-${ed}`;
};

const currentMonthLocal = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
};

const currentMonthStartDayjs = () => dayjs().startOf('month');

const twelveMonthsAgoStartDayjs = () => dayjs().startOf('month').subtract(12, 'month');

const previousMonthStartDayjs = () => dayjs().startOf('month').subtract(1, 'month');

const yesterdayDayjs = () => dayjs().startOf('day').subtract(1, 'day');

const twelveMonthsAgoDayStartDayjs = () => dayjs().startOf('day').subtract(12, 'month');

// Snap any dayjs to the Monday of its week without relying on locale or plugins.
const snapToMonday = (value) => {
    const day = value.day();
    const offset = day === 0 ? -6 : 1 - day;
    return value.add(offset, 'day').startOf('day');
};

const lastCompletedMondayDayjs = () => {
    const today = dayjs().startOf('day');
    const thisMonday = snapToMonday(today);
    return thisMonday.subtract(7, 'day');
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatMonthLabel = (month) => {
    if (!month || !/^\d{4}-\d{2}$/.test(month)) return month || '';
    const [y, m] = month.split('-').map(Number);
    const partial = month === currentMonthLocal() ? ' (month-to-date)' : '';
    return `${MONTH_NAMES[m - 1]} ${y}${partial}`;
};

const omit = (props) => (prop) => !props.includes(prop);

const AppBarStyled = styled(AppBar)(({ theme }) => ({
    flexGrow: 1,
    zIndex: theme.zIndex.drawer + 1,
}));

const MenuButtonStyled = styled(IconButton, { shouldForwardProp: omit(['mobileSearchOpen']) })(({ theme, mobileSearchOpen }) => ({
    display: mobileSearchOpen ? 'none' : 'block',
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
        display: 'none',
    },
}));

const CollapseNavButtonStyled = styled(IconButton)(({ theme }) => ({
    display: 'none',
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
        display: 'inline-flex',
    },
}));

const TitleStyled = styled(Typography)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('sm')]: {
        display: 'block'
    }
}));

const SearchStyled = styled('div', { shouldForwardProp: omit(['mobileSearchOpen']) })(({ theme, mobileSearchOpen }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(grey[500], 0.15),
    '&:hover': {
        backgroundColor: alpha(grey[400], 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    display: mobileSearchOpen ? 'flex' : 'none',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
        width: 'auto',
        flexGrow: 1,
        display: 'flex',
    }
}));

const SearchIconStyled = styled('div')(({ theme }) => ({
    padding: theme.spacing(2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
}));

const SearchInputStyled = styled(InputBase)(({ theme }) => ({
    padding: theme.spacing(0.2, 1, 0.2),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    flexGrow: 1,
}));

const MobileSearchCloseStyled = styled(IconButton)(({ theme }) => ({
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.up('sm')]: {
        display: 'none',
    },
}));

const SectionActionsStyled = styled('div', { shouldForwardProp: omit(['mobileSearchOpen']) })(({ theme, mobileSearchOpen }) => ({
    display: mobileSearchOpen ? 'none' : 'flex',
    marginLeft: 'auto',
    alignItems: 'center',
}));

const MobileSearchTriggerStyled = styled(IconButton)(({ theme }) => ({
    [theme.breakpoints.up('sm')]: {
        display: 'none',
    },
}));

const EMPTY_RESULTS = { clients: [], accounts: [], projects: [], tasks: [], groups: [], users: [], totals: {} };

const NOTIFICATION_NO_RECORD_MESSAGE = 'This AI summary has expired from the cache. Open the AI menu and pick the same period to generate a fresh one.';

const EOD_NO_RECORD_MESSAGE = 'This end-of-day checklist is no longer available — each checklist is only available for the rest of the day it was sent. A new one will arrive shortly before your next working day ends.';

const Navbar = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();
    const brandName = useBrandName();
    const unreadCount = useSelector(state => state.notifications.unreadCount);
    const themeMode = useSelector((state) => state.ui.theme.mode);
    const navCollapsed = useSelector((state) => state.ui.navCollapsed);
    const hasPermission = useHasPermission();
    const canSeeManagement = hasPermission('canSeeManagement');

    const handleToggleDrawer = () => {
        dispatch(toggleNavDrawer());
    };

    const handleToggleCollapsed = () => {
        dispatch(toggleNavCollapsed());
    };

    const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
    const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
    const [aiMenuAnchorEl, setAiMenuAnchorEl] = useState(null);
    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const [aiDate, setAiDate] = useState(null);
    const [aiRecord, setAiRecord] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiError, setAiError] = useState(null);
    const [aiWeekDialogOpen, setAiWeekDialogOpen] = useState(false);
    const [aiWeekStart, setAiWeekStart] = useState(null);
    const [aiWeekRecord, setAiWeekRecord] = useState(null);
    const [aiWeekBusy, setAiWeekBusy] = useState(false);
    const [aiWeekError, setAiWeekError] = useState(null);
    const [aiMonthDialogOpen, setAiMonthDialogOpen] = useState(false);
    const [aiMonth, setAiMonth] = useState(null);
    const [aiMonthRecord, setAiMonthRecord] = useState(null);
    const [aiMonthBusy, setAiMonthBusy] = useState(false);
    const [aiMonthError, setAiMonthError] = useState(null);
    const [eodDialogOpen, setEodDialogOpen] = useState(false);
    const [eodDate, setEodDate] = useState(null);
    const [eodRecord, setEodRecord] = useState(null);
    const [eodBusy, setEodBusy] = useState(false);
    const [eodError, setEodError] = useState(null);
    const [dayPickerAnchorEl, setDayPickerAnchorEl] = useState(null);
    const [dayPickerValue, setDayPickerValue] = useState(yesterdayDayjs());
    const [weekPickerAnchorEl, setWeekPickerAnchorEl] = useState(null);
    const [weekPickerValue, setWeekPickerValue] = useState(lastCompletedMondayDayjs());
    const [monthPickerAnchorEl, setMonthPickerAnchorEl] = useState(null);
    const [monthPickerValue, setMonthPickerValue] = useState(previousMonthStartDayjs());
    const isAiMenuOpen = Boolean(aiMenuAnchorEl);
    const isDayPickerOpen = Boolean(dayPickerAnchorEl);
    const isWeekPickerOpen = Boolean(weekPickerAnchorEl);
    const isMonthPickerOpen = Boolean(monthPickerAnchorEl);

    const dayPickerBounds = useMemo(() => ({
        min: twelveMonthsAgoDayStartDayjs(),
        max: yesterdayDayjs(),
    }), []);
    const weekPickerBounds = useMemo(() => ({
        min: twelveMonthsAgoDayStartDayjs(),
        max: lastCompletedMondayDayjs().add(6, 'day'),
    }), []);
    const monthPickerBounds = useMemo(() => ({
        min: twelveMonthsAgoStartDayjs(),
        max: currentMonthStartDayjs(),
    }), []);

    const handleAiMenuOpen = (event) => setAiMenuAnchorEl(event.currentTarget);
    const handleAiMenuClose = () => setAiMenuAnchorEl(null);

    const fetchDailyActivityState = useCallback((date) => {
        return new Promise((resolve) => {
            dispatch(getDailyActivityAiSummary(date, (resp) => {
                if (resp?.ok) {
                    const record = resp.data?.exists ? resp.data.record : null;
                    setAiRecord(record);
                    setAiError(null);
                    resolve(record);
                } else {
                    setAiError(resp?.message || 'Failed to read daily activity summary state');
                    resolve(null);
                }
            }));
        });
    }, [dispatch]);

    const requestDailyActivity = useCallback((date) => {
        setAiBusy(true);
        setAiError(null);
        return new Promise((resolve) => {
            dispatch(requestDailyActivityAiSummary(date, (resp) => {
                if (resp?.ok) {
                    setAiRecord(resp.data?.record || null);
                } else {
                    setAiError(resp?.message || 'Failed to request daily activity summary');
                }
                setAiBusy(false);
                resolve();
            }));
        });
    }, [dispatch]);

    const openDayPicker = useCallback((event) => {
        handleAiMenuClose();
        setDayPickerValue(yesterdayDayjs());
        setDayPickerAnchorEl(event.currentTarget);
    }, []);

    const closeDayPicker = useCallback(() => {
        setDayPickerAnchorEl(null);
    }, []);

    const submitDayPicker = useCallback(async () => {
        if (!dayPickerValue || !dayjs.isDayjs(dayPickerValue) || !dayPickerValue.isValid()) return;
        const date = dayPickerValue.format('YYYY-MM-DD');
        closeDayPicker();
        if (aiBusy) return;
        setAiDate(date);
        setAiDialogOpen(true);
        setAiBusy(true);
        const existing = await fetchDailyActivityState(date);
        setAiBusy(false);
        if (!existing) {
            await requestDailyActivity(date);
        }
    }, [dayPickerValue, aiBusy, closeDayPicker, fetchDailyActivityState, requestDailyActivity]);

    const closeAiDialog = () => {
        setAiDialogOpen(false);
        setAiRecord(null);
        setAiError(null);
    };
    const retryDailyActivity = useCallback(() => {
        if (aiDate) requestDailyActivity(aiDate);
    }, [aiDate, requestDailyActivity]);

    useEffect(() => {
        if (!aiDialogOpen || !aiDate) return;
        const socket = getSocket();
        if (!socket) return;
        const handler = (payload) => {
            if (payload?.kind !== 'daily-activity-summary') return;
            if (payload?.contextId !== aiDate) return;
            fetchDailyActivityState(aiDate);
        };
        socket.on('aiResponse:ready', handler);
        return () => { socket.off('aiResponse:ready', handler); };
    }, [aiDialogOpen, aiDate, fetchDailyActivityState]);

    const fetchWeeklyActivityState = useCallback((weekStart) => {
        return new Promise((resolve) => {
            dispatch(getWeeklyActivityAiSummary(weekStart, (resp) => {
                if (resp?.ok) {
                    const record = resp.data?.exists ? resp.data.record : null;
                    setAiWeekRecord(record);
                    setAiWeekError(null);
                    resolve(record);
                } else {
                    setAiWeekError(resp?.message || 'Failed to read weekly activity summary state');
                    resolve(null);
                }
            }));
        });
    }, [dispatch]);

    const requestWeeklyActivity = useCallback((weekStart) => {
        setAiWeekBusy(true);
        setAiWeekError(null);
        return new Promise((resolve) => {
            dispatch(requestWeeklyActivityAiSummary(weekStart, (resp) => {
                if (resp?.ok) {
                    setAiWeekRecord(resp.data?.record || null);
                } else {
                    setAiWeekError(resp?.message || 'Failed to request weekly activity summary');
                }
                setAiWeekBusy(false);
                resolve();
            }));
        });
    }, [dispatch]);

    const openWeekPicker = useCallback((event) => {
        handleAiMenuClose();
        setWeekPickerValue(lastCompletedMondayDayjs());
        setWeekPickerAnchorEl(event.currentTarget);
    }, []);

    const closeWeekPicker = useCallback(() => {
        setWeekPickerAnchorEl(null);
    }, []);

    const submitWeekPicker = useCallback(async () => {
        if (!weekPickerValue || !dayjs.isDayjs(weekPickerValue) || !weekPickerValue.isValid()) return;
        const monday = snapToMonday(weekPickerValue);
        const weekStart = monday.format('YYYY-MM-DD');
        closeWeekPicker();
        if (aiWeekBusy) return;
        setAiWeekStart(weekStart);
        setAiWeekDialogOpen(true);
        setAiWeekBusy(true);
        const existing = await fetchWeeklyActivityState(weekStart);
        setAiWeekBusy(false);
        if (!existing) {
            await requestWeeklyActivity(weekStart);
        }
    }, [weekPickerValue, aiWeekBusy, closeWeekPicker, fetchWeeklyActivityState, requestWeeklyActivity]);

    const closeWeeklyDialog = () => {
        setAiWeekDialogOpen(false);
        setAiWeekRecord(null);
        setAiWeekError(null);
    };
    const retryWeeklyActivity = useCallback(() => {
        if (aiWeekStart) requestWeeklyActivity(aiWeekStart);
    }, [aiWeekStart, requestWeeklyActivity]);

    useEffect(() => {
        if (!aiWeekDialogOpen || !aiWeekStart) return;
        const socket = getSocket();
        if (!socket) return;
        const handler = (payload) => {
            if (payload?.kind !== 'weekly-activity-summary') return;
            if (payload?.contextId !== aiWeekStart) return;
            fetchWeeklyActivityState(aiWeekStart);
        };
        socket.on('aiResponse:ready', handler);
        return () => { socket.off('aiResponse:ready', handler); };
    }, [aiWeekDialogOpen, aiWeekStart, fetchWeeklyActivityState]);

    const fetchMonthlyActivityState = useCallback((month) => {
        return new Promise((resolve) => {
            dispatch(getMonthlyActivityAiSummary(month, (resp) => {
                if (resp?.ok) {
                    const record = resp.data?.exists ? resp.data.record : null;
                    setAiMonthRecord(record);
                    setAiMonthError(null);
                    resolve(record);
                } else {
                    setAiMonthError(resp?.message || 'Failed to read monthly activity summary state');
                    resolve(null);
                }
            }));
        });
    }, [dispatch]);

    const requestMonthlyActivity = useCallback((month) => {
        setAiMonthBusy(true);
        setAiMonthError(null);
        return new Promise((resolve) => {
            dispatch(requestMonthlyActivityAiSummary(month, (resp) => {
                if (resp?.ok) {
                    setAiMonthRecord(resp.data?.record || null);
                } else {
                    setAiMonthError(resp?.message || 'Failed to request monthly activity summary');
                }
                setAiMonthBusy(false);
                resolve();
            }));
        });
    }, [dispatch]);

    const openMonthPicker = useCallback((event) => {
        handleAiMenuClose();
        setMonthPickerValue(previousMonthStartDayjs());
        setMonthPickerAnchorEl(event.currentTarget);
    }, []);

    const closeMonthPicker = useCallback(() => {
        setMonthPickerAnchorEl(null);
    }, []);

    const submitMonthPicker = useCallback(async () => {
        if (!monthPickerValue || !dayjs.isDayjs(monthPickerValue) || !monthPickerValue.isValid()) return;
        const month = monthPickerValue.format('YYYY-MM');
        closeMonthPicker();
        if (aiMonthBusy) return;
        setAiMonth(month);
        setAiMonthDialogOpen(true);
        setAiMonthBusy(true);
        const existing = await fetchMonthlyActivityState(month);
        setAiMonthBusy(false);
        if (!existing) {
            await requestMonthlyActivity(month);
        }
    }, [monthPickerValue, aiMonthBusy, closeMonthPicker, fetchMonthlyActivityState, requestMonthlyActivity]);

    const closeMonthlyDialog = useCallback(() => {
        setAiMonthDialogOpen(false);
        setAiMonthRecord(null);
        setAiMonthError(null);
        setAiMonth(null);
    }, []);
    const retryMonthlyActivity = useCallback(() => {
        if (aiMonth) requestMonthlyActivity(aiMonth);
    }, [aiMonth, requestMonthlyActivity]);

    useEffect(() => {
        if (!aiMonthDialogOpen || !aiMonth) return;
        const socket = getSocket();
        if (!socket) return;
        const handler = (payload) => {
            if (payload?.kind !== 'monthly-activity-summary') return;
            if (payload?.contextId !== aiMonth) return;
            fetchMonthlyActivityState(aiMonth);
        };
        socket.on('aiResponse:ready', handler);
        return () => { socket.off('aiResponse:ready', handler); };
    }, [aiMonthDialogOpen, aiMonth, fetchMonthlyActivityState]);

    const fetchEodChecklistState = useCallback((date) => {
        return new Promise((resolve) => {
            dispatch(getEndOfDayChecklist(date, (resp) => {
                if (resp?.ok) {
                    const record = resp.data?.exists ? resp.data.record : null;
                    setEodRecord(record);
                    setEodError(null);
                    setEodBusy(false);
                    resolve({ ok: true, record });
                } else {
                    setEodError(resp?.message || 'We could not load your end-of-day checklist. Please try again in a moment.');
                    setEodBusy(false);
                    resolve({ ok: false, record: null });
                }
            }));
        });
    }, [dispatch]);

    const closeEodDialog = useCallback(() => {
        setEodDialogOpen(false);
        setEodRecord(null);
        setEodError(null);
        setEodDate(null);
    }, []);

    useEffect(() => {
        if (!eodDialogOpen || !eodDate) return;
        const socket = getSocket();
        if (!socket) return;
        const handler = (payload) => {
            if (payload?.kind !== 'end-of-day-checklist') return;
            if (payload?.contextId !== eodDate) return;
            fetchEodChecklistState(eodDate);
        };
        socket.on('aiResponse:ready', handler);
        return () => { socket.off('aiResponse:ready', handler); };
    }, [eodDialogOpen, eodDate, fetchEodChecklistState]);

    const dailyParam = searchParams.get('dailyActivity');
    const weeklyParam = searchParams.get('weeklyActivity');
    const monthlyParam = searchParams.get('monthlyActivity');
    const eodParam = searchParams.get('eodChecklist');

    useEffect(() => {
        if (!dailyParam && !weeklyParam && !monthlyParam && !eodParam) return;
        const next = new URLSearchParams(searchParams);
        next.delete('dailyActivity');
        next.delete('weeklyActivity');
        next.delete('monthlyActivity');
        next.delete('eodChecklist');

        if (dailyParam) {
            setAiDate(dailyParam);
            setAiRecord(null);
            setAiError(null);
            setAiDialogOpen(true);
            fetchDailyActivityState(dailyParam).then((record) => {
                if (!record) setAiError(NOTIFICATION_NO_RECORD_MESSAGE);
            });
        } else if (weeklyParam) {
            setAiWeekStart(weeklyParam);
            setAiWeekRecord(null);
            setAiWeekError(null);
            setAiWeekDialogOpen(true);
            fetchWeeklyActivityState(weeklyParam).then((record) => {
                if (!record) setAiWeekError(NOTIFICATION_NO_RECORD_MESSAGE);
            });
        } else if (monthlyParam) {
            setAiMonth(monthlyParam);
            setAiMonthRecord(null);
            setAiMonthError(null);
            setAiMonthDialogOpen(true);
            fetchMonthlyActivityState(monthlyParam).then((record) => {
                if (!record) setAiMonthError(NOTIFICATION_NO_RECORD_MESSAGE);
            });
        } else if (eodParam) {
            setEodDate(eodParam);
            setEodRecord(null);
            setEodError(null);
            setEodBusy(true);
            setEodDialogOpen(true);
            fetchEodChecklistState(eodParam).then((result) => {
                if (result?.ok && !result.record) setEodError(EOD_NO_RECORD_MESSAGE);
            });
        }

        setSearchParams(next, { replace: true });
    }, [dailyParam, weeklyParam, monthlyParam, eodParam, searchParams, setSearchParams, fetchDailyActivityState, fetchWeeklyActivityState, fetchMonthlyActivityState, fetchEodChecklistState]);

    // Search state
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(EMPTY_RESULTS);
    const [loading, setLoading] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const searchRef = useRef(null);
    const searchInputRef = useRef(null);
    const debounceRef = useRef(null);

    const isProfileMenuOpen = Boolean(profileMenuAnchorEl);
    const isNotificationOpen = Boolean(notificationAnchorEl);

    const handleProfileMenuOpen = (event) => {
        setProfileMenuAnchorEl(event.currentTarget);
    };

    const handleNotificationOpen = (event) => {
        setNotificationAnchorEl(event.currentTarget);
    };

    const handleNotificationClose = () => {
        setNotificationAnchorEl(null);
    };

    const handleProfileMenuClose = () => {
        setProfileMenuAnchorEl(null);
    };

    const handleMenuClick = (pageURL) => {
        navigate(pageURL);
        handleProfileMenuClose();
    };

    const runSearch = useCallback((q, type) => {
        if (!q || q.trim().length < 2) {
            setResults(EMPTY_RESULTS);
            setLoading(false);
            return;
        }
        setLoading(true);
        dispatch(search(q.trim(), type, (data) => {
            setResults(data);
            setLoading(false);
        }));
    }, [dispatch]);

    const handleQueryChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        clearTimeout(debounceRef.current);
        if (!value || value.trim().length < 2) {
            setResults(EMPTY_RESULTS);
            setSearchOpen(false);
            setLoading(false);
            return;
        }
        setSearchOpen(true);
        setLoading(true);
        debounceRef.current = setTimeout(() => {
            runSearch(value, activeTab);
        }, 300);
    };

    const handleSearchFocus = () => {
        if (query.trim().length >= 2) {
            setSearchOpen(true);
        }
    };

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        runSearch(query, newTab);
    };

    const handleMobileSearchOpen = () => {
        setMobileSearchOpen(true);
    };

    const handleMobileSearchClose = () => {
        setMobileSearchOpen(false);
        setSearchOpen(false);
        setQuery('');
        setResults(EMPTY_RESULTS);
        clearTimeout(debounceRef.current);
    };

    const handleSearchClose = () => {
        setSearchOpen(false);
        if (mobileSearchOpen) {
            handleMobileSearchClose();
        }
    };

    useEffect(() => {
        if (mobileSearchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [mobileSearchOpen]);

    const handleViewAll = (q, type) => {
        const params = new URLSearchParams({ q, type });
        navigate(`/search?${params.toString()}`);
    };

    // Clean up debounce on unmount
    useEffect(() => {
        return () => clearTimeout(debounceRef.current);
    }, []);

    const menuId = 'appbar-profile-menu';
    const renderProfileMenu = (
        <Menu
            anchorEl={profileMenuAnchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            id={menuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isProfileMenuOpen}
            onClose={handleProfileMenuClose}
        >
            <MenuItem onClick={() => handleMenuClick('/profile')}>Profile</MenuItem>
            <MenuItem onClick={() => handleMenuClick('/settings')}>Settings</MenuItem>
            <MenuItem onClick={() => handleMenuClick('/logout')}>Log Out</MenuItem>
        </Menu>
    );

    return (
        <>
            <AppBarStyled position="sticky" color="default">
                <Toolbar>
                    <MenuButtonStyled
                        edge="start"
                        onClick={handleToggleDrawer}
                        color="inherit"
                        aria-label="open drawer"
                        size="large"
                        mobileSearchOpen={mobileSearchOpen}
                    >
                        <MenuIcon />
                    </MenuButtonStyled>
                    <Tooltip title={navCollapsed ? "Expand menu" : "Collapse menu"}>
                        <CollapseNavButtonStyled
                            edge="start"
                            onClick={handleToggleCollapsed}
                            color="inherit"
                            aria-label={navCollapsed ? "Expand menu" : "Collapse menu"}
                            size="large"
                        >
                            {navCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
                        </CollapseNavButtonStyled>
                    </Tooltip>
                    <TitleStyled variant="h6" onClick={() => navigate("/")}>
                        {brandName}
                    </TitleStyled>
                    <SearchStyled ref={searchRef} mobileSearchOpen={mobileSearchOpen}>
                        <SearchIconStyled>
                            <SearchIcon />
                        </SearchIconStyled>
                        <SearchInputStyled
                            placeholder="Search clients, accounts, projects, users..."
                            fullWidth
                            value={query}
                            onChange={handleQueryChange}
                            onFocus={handleSearchFocus}
                            inputRef={searchInputRef}
                            inputProps={{ 'aria-label': 'search' }}
                        />
                        {mobileSearchOpen && (
                            <MobileSearchCloseStyled
                                aria-label="close search"
                                onClick={handleMobileSearchClose}
                                size="small"
                            >
                                <CloseIcon fontSize="small" />
                            </MobileSearchCloseStyled>
                        )}
                    </SearchStyled>
                    <SectionActionsStyled mobileSearchOpen={mobileSearchOpen}>
                        <MobileSearchTriggerStyled
                            aria-label="open search"
                            color="inherit"
                            size="large"
                            onClick={handleMobileSearchOpen}
                        >
                            <SearchIcon />
                        </MobileSearchTriggerStyled>
                        {canSeeManagement && (
                            <IconButton
                                aria-label="AI tools"
                                aria-controls="appbar-ai-menu"
                                aria-haspopup="true"
                                color="inherit"
                                size="large"
                                onClick={handleAiMenuOpen}
                            >
                                <AutoAwesome />
                            </IconButton>
                        )}
                        <Tooltip title={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
                            <IconButton aria-label="toggle theme" color="inherit" size="large" onClick={() => dispatch(toggleTheme())}>
                                {themeMode === "dark" ? <Brightness7 /> : <Brightness4 />}
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            aria-label="show new notifications"
                            color="inherit"
                            size="large"
                            onClick={handleNotificationOpen}
                        >
                            <Badge badgeContent={unreadCount} max={99} color="error">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                        <IconButton
                            edge="end"
                            aria-label="account of current user"
                            aria-controls={menuId}
                            aria-haspopup="true"
                            onClick={handleProfileMenuOpen}
                            color="inherit"
                            size="large"
                        >
                            <AccountCircle />
                        </IconButton>
                    </SectionActionsStyled>
                </Toolbar>
            </AppBarStyled>
            {renderProfileMenu}
            {canSeeManagement && (
                <Menu
                    id="appbar-ai-menu"
                    anchorEl={aiMenuAnchorEl}
                    open={isAiMenuOpen}
                    onClose={handleAiMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    keepMounted
                >
                    <MenuItem onClick={openDayPicker}>Daily team activity</MenuItem>
                    <MenuItem onClick={openWeekPicker}>Weekly team activity</MenuItem>
                    <MenuItem onClick={openMonthPicker}>Monthly team activity</MenuItem>
                </Menu>
            )}
            <ActivitySummaryPicker
                open={isDayPickerOpen}
                anchorEl={dayPickerAnchorEl}
                onClose={closeDayPicker}
                title="Daily team activity"
                helperText="Pick a day. The AI will generate a summary of team activity for that day."
                value={dayPickerValue}
                onChange={(value) => setDayPickerValue(value)}
                minDate={dayPickerBounds.min}
                maxDate={dayPickerBounds.max}
                views={['year', 'month', 'day']}
                openTo="day"
                format="MMMM D, YYYY"
                pickerLabel="Day"
                onSubmit={submitDayPicker}
                submitDisabled={aiBusy}
            />
            <ActivitySummaryPicker
                open={isWeekPickerOpen}
                anchorEl={weekPickerAnchorEl}
                onClose={closeWeekPicker}
                title="Weekly team activity"
                helperText="Pick any day in the week. The AI will summarize team activity for that week."
                value={weekPickerValue}
                onChange={(value) => setWeekPickerValue(value)}
                minDate={weekPickerBounds.min}
                maxDate={weekPickerBounds.max}
                views={['year', 'month', 'day']}
                openTo="day"
                format="MMMM D, YYYY"
                pickerLabel="Day in week"
                onSubmit={submitWeekPicker}
                submitDisabled={aiWeekBusy}
            />
            <ActivitySummaryPicker
                open={isMonthPickerOpen}
                anchorEl={monthPickerAnchorEl}
                onClose={closeMonthPicker}
                title="Monthly team activity"
                helperText="Pick a month. The AI will generate a summary of team activity for that month."
                value={monthPickerValue}
                onChange={(value) => setMonthPickerValue(value)}
                minDate={monthPickerBounds.min}
                maxDate={monthPickerBounds.max}
                views={['year', 'month']}
                openTo="month"
                format="MMMM YYYY"
                pickerLabel="Month"
                onSubmit={submitMonthPicker}
                submitDisabled={aiMonthBusy}
            />
            <AIResponseDialog
                open={aiDialogOpen}
                onClose={closeAiDialog}
                title="Daily team activity"
                subtitle={aiDate ? dayjs(aiDate, 'YYYY-MM-DD').format('MMMM D, YYYY') : undefined}
                record={aiRecord}
                busy={aiBusy}
                error={aiError}
                onRetry={retryDailyActivity}
            />
            <AIResponseDialog
                open={aiWeekDialogOpen}
                onClose={closeWeeklyDialog}
                title="Weekly team activity"
                subtitle={aiWeekStart ? `Week of ${aiWeekStart} to ${sundayForMondayLocal(aiWeekStart)}` : undefined}
                record={aiWeekRecord}
                busy={aiWeekBusy}
                error={aiWeekError}
                onRetry={retryWeeklyActivity}
            />
            <AIResponseDialog
                open={aiMonthDialogOpen}
                onClose={closeMonthlyDialog}
                title="Monthly team activity"
                subtitle={aiMonth ? formatMonthLabel(aiMonth) : undefined}
                record={aiMonthRecord}
                busy={aiMonthBusy}
                error={aiMonthError}
                onRetry={retryMonthlyActivity}
            />
            <AIResponseDialog
                open={eodDialogOpen}
                onClose={closeEodDialog}
                title="End-of-day checklist"
                subtitle={eodDate ? formatDayLong(eodDate) : undefined}
                record={eodRecord}
                busy={eodBusy}
                error={eodError}
            />
            <Popover
                open={isNotificationOpen}
                anchorEl={notificationAnchorEl}
                onClose={handleNotificationClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ elevation: 3 }}
            >
                <NotificationPanel onClose={handleNotificationClose} />
            </Popover>
            <SearchModal
                anchorEl={searchRef.current}
                open={searchOpen}
                onClose={handleSearchClose}
                results={results}
                loading={loading}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                query={query}
                onViewAll={handleViewAll}
            />
        </>
    );
};

export default Navbar;
